import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "./api/client";
import { TopBar } from "./components/shell/TopBar";
import { LoginPage } from "./pages/Login";
import { DocumentsList } from "./pages/DocumentsList";
import { PackageConfigurationPage } from "./pages/PackageConfiguration";
import { UploadPage } from "./pages/Upload";
import { WizardPage } from "./pages/Wizard";
import { ReportPage } from "./pages/Report";
import { ComparePage } from "./pages/Compare";
import { clearUser, identifyUser, trackPageView } from "./telemetry";

function App() {
  const location = useLocation();
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
  });
  const user = meQuery.data;
  // `__MODELS__` in the browser console says which model serves each role on
  // this deployment.
  const modelsQuery = useQuery({ queryKey: ["models"], queryFn: api.models, enabled: !!user });

  useEffect(() => {
    if (modelsQuery.data) window.__MODELS__ = modelsQuery.data;
  }, [modelsQuery.data]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (user) identifyUser(user);
    else if (meQuery.error instanceof ApiError && meQuery.error.status === 401) clearUser();
  }, [meQuery.error, user]);

  if (meQuery.isLoading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
    return <LoginPage onLoggedIn={() => meQuery.refetch()} />;
  }

  if (!user) {
    return <LoginPage onLoggedIn={() => meQuery.refetch()} />;
  }

  return (
    <div className="app-simple">
      <RoutedTopBar />
      <div className="workspace">
        <Routes>
          <Route path="/" element={<DocumentsList />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/docs/:docId/package" element={<PackageConfigurationPage />} />
          <Route
            path="/docs/:docId/wizard/:rowKey?"
            element={<WizardPage />}
          />
          <Route path="/docs/:docId/report" element={<ReportPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function RoutedTopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return <TopBar onTitleClick={() => navigate("/")} showHome={pathname !== "/"} />;
}

export default App;
